/**
 * @file   src\components\Inventory\EditInventory\InventoryLocationMap.js
 * @brief  This file is responsible for the view of inventory location section.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import InputGroupControl from "@/components/Common/InputGroup";
import {
  capitalizeString,
  handleTextBoxChange,
} from "@/actions/common";
import {
  getLocalStorage,
  removeFixedDecimalPart,
  trimString,
  formatAddressForDisplay,
} from "@/helpers/common";
import MapBoxComponent from "@/components/Common/MapboxComponent";
import {
  getGeoLocation,
  getPropertyDetails,
  getPropertySearch,
} from "@/services/commonServices";
import { Button } from "react-bootstrap";
import {
  INVENTORY_MAP_RADIUS,
  MAP_EVENT_CLICK,
  MAP_EVENT_LOCATION_CHANGE,
  MAP_EVENT_MARKER_DRAG,
} from "@/constants/common";

const InventoryLocationMap = (props) => {
  const {
    handleFormValues,
    locationAddress,
    defaultProps,
    handlePropertyExist,
    isLot_Land,
    isPrivateRoom,
    isAgentCreated = false,
    locationError,
    sessionKey,
    disabled = false,
  } = props;

  // Initialize component state variables and constants.
  const [addressList, setAddressList] = useState([]);
  const [showMarker, setShowMarker] = useState(true);
  const [propertyAddress, setPropertyAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [isClickOrDrag, setClickDrag] = useState(false);

  // Reference
  const addressRef = useRef();

  // Handle the marker in google map
  const handleShowMarker = (markerStatus) => {
    setShowMarker(markerStatus);
  };

  // To handle the click in map
  const HandleMapClickOrDrag = (status) => {
    setClickDrag(status);
  };

  // check the property exist or not
  useEffect(() => {
    handlePropertyExistCheck();
  }, []);

  // check the property exist or not
  const handlePropertyExistCheck = () => {
    let isExist;
    if (trimString(locationAddress) === "") {
      isExist = false;
    } else {
      isExist = true;
    }
    handleFormValues({ isPropertyExist: isExist });
  };


  // To handle the address selection
  const handleItemSelection = (address) => {
    handleShowMarker(true);
    HandleMapClickOrDrag(false);
    const formatted_address = _.get(address, "formatted_address", "");
    const latitude = _.get(address, "geometry.location.lat", "");
    const longitude = _.get(address, "geometry.location.lng", "");
    const reqPayLoad = {
      latitude: latitude,
      longitude: longitude,
      radius: INVENTORY_MAP_RADIUS,
      mls_active: true,
    };
    handleFormValues({
      locAddress: formatted_address?.trim(),
      lat: latitude,
      lng: longitude,
    });
    getPropertySearchDetails(reqPayLoad);
    clearAddressList();
  };

  // To clear the location state values
  const clearLocationStates = () => {
    handleFormValues({
      noBedRm: "",
      noBathRm: "",
      sqFt: "",
      stories: "",
      yearBuilt: "",
      neighborHood: "",
      lotSize: "",
      highSchool: "",
      isPropertyExist: "",
      locAddress: "",
      street: "",
      city: "",
      county: "",
      country: "",
      state: "",
      zip: "",
      street_no: "",
      route: "",
    });
  };

  // To get the property details like schools, stories and highschool values
  const getProprtyDetailsBasedOnPropertyId = async (reqPayload) => {
    if (!isLot_Land) {
      await getPropertyDetails(reqPayload).then((data) => {
        const result = _.get(data, "result.data");
        const propertyInfo = _.get(result, "propertyInfo");
        if (result) {
          let stateToBeUpdated = {};
          const schools = _.get(result, "schools", []);
          if (propertyInfo) {
            stateToBeUpdated.stories = _.get(propertyInfo, "stories", "");
          }
          if (schools.length > 0) {
            stateToBeUpdated.highSchool = _.get(result, "schools[0].name", "");
          }

          handleFormValues(stateToBeUpdated);
        }
      });
    }
  };

  // To clear the address list
  const clearAddressList = () => {
    setAddressList([]);
  };

  // On location change
  const handleLocationChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    if (value === "") {
      clearLocationStates();
    } else {
      const reqPayload = `address=${value}&components=country:US`;
      getAddresseDetails({
        eventType: MAP_EVENT_LOCATION_CHANGE,
        reqPayload: reqPayload,
        address: value,
      });
    }
    handleFormValues({ locAddress: value });
  };

  // on map event change/ on location change get the property details
  const getPropertySearchDetails = async (reqPayLoad) => {
    setPropertyAddress("");
    const inventoryDetails = getLocalStorage(sessionKey);
    const street_no = _.get(inventoryDetails, "street_no", null);
    const route = _.get(inventoryDetails, "route", null);
    let streetAddress = null;
    if (street_no && route) {
      streetAddress = street_no + " " + route;
    } else if (street_no && !route) {
      streetAddress = street_no;
    } else if (!street_no && route) {
      streetAddress = route;
    }
    const isStreetExist = reqPayLoad.street;
    if (!isStreetExist) {
      reqPayLoad.street = streetAddress;
    }
    if (!isPrivateRoom) {
      try {
        await getPropertySearch(reqPayLoad).then((data) => {
          let statesToBeUpdated = { isPropertyExist: true };
          statesToBeUpdated.sellAskngPrice = "";
          handlePropertyExist({ isMapValidLocation: true });
          const searchData = _.get(data, "result");
          if (searchData) {
            statesToBeUpdated.zestimate = _.get(searchData, "zestimate", null);
            const lotSquareFeet = _.get(searchData, "lotSquareFeet", null);
            const proprtyAddrs = _.get(searchData, "address", "");
            const mlsActive = _.get(searchData, "mlsActive", "");
            if (mlsActive) {
              statesToBeUpdated.sellAskngPrice = _.get(
                searchData,
                "mlsListingPrice",
                null,
              );
            }

            if (!isLot_Land) {
              setPropertyAddress(" (" + _.get(proprtyAddrs, "address", "") + ")");
              const yearBuilt = _.get(searchData, "yearBuilt", null);
              const neighborhood = _.get(searchData, "neighborhood", null);

              let lotInAcres = null;
              if (lotSquareFeet && lotSquareFeet !== 0) {
                try {
                  lotInAcres = lotSquareFeet / 43560;
                } catch {}
              }

              statesToBeUpdated.noBedRm = _.get(searchData, "bedrooms", "");
              statesToBeUpdated.noBathRm = _.get(searchData, "bathrooms", "");
              statesToBeUpdated.sqFt = _.get(searchData, "squareFeet", "");
              (statesToBeUpdated.yearBuilt = yearBuilt),
                (statesToBeUpdated.neighborHood = _.get(
                  neighborhood,
                  "name",
                  null,
                ));
              statesToBeUpdated.lotSize =
                lotInAcres &&
                removeFixedDecimalPart({ value: lotInAcres, decimalPoint: 2 });

              statesToBeUpdated.street = _.get(proprtyAddrs, "street", "");
              (statesToBeUpdated.city = _.get(proprtyAddrs, "city", "")),
                (statesToBeUpdated.county = _.get(proprtyAddrs, "county", ""));
              statesToBeUpdated.state = _.get(proprtyAddrs, "state", "");
              statesToBeUpdated.zip = _.get(proprtyAddrs, "zip", "");

              const propertyId =
                _.get(searchData, "propertyId", "") ||
                _.get(searchData, "id", "");
              getProprtyDetailsBasedOnPropertyId({ propertyId: propertyId });
            } else {
              statesToBeUpdated.acreage = lotSquareFeet;
              statesToBeUpdated.acre_Sqft = 2;
              statesToBeUpdated.street = _.get(proprtyAddrs, "street", "");
              statesToBeUpdated.city = _.get(proprtyAddrs, "city", "");
              statesToBeUpdated.county = _.get(proprtyAddrs, "county", "");
              statesToBeUpdated.state = _.get(proprtyAddrs, "state", "");
              statesToBeUpdated.zip = _.get(proprtyAddrs, "zip", "");
            }
          }
          handleFormValues(statesToBeUpdated);
        });
      } catch (error) {
        console.log("Error on getting property details", error);
      }
    }
  };

  // To get the address details
  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    handleFormValues({
      noBedRm: "",
      noBathRm: "",
      sqFt: "",
      stories: "",
      yearBuilt: "",
      neighborHood: "",
      lotSize: "",
      highSchool: "",
      isPropertyExist: "",
      street: "",
      city: "",
      county: "",
      country: "",
      state: "",
      zip: "",
      street_no: "",
      route: "",
    });

    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (
          eventType === MAP_EVENT_LOCATION_CHANGE &&
          results?.length === 1 &&
          formatted_address !== "United States"
        ) {
          setAddressList(results);
        }
        const firstLocationResult = _.get(results[0], "geometry.location", "");
        const firstAddressComponent = _.get(
          results[0],
          "address_components",
          "",
        );

        const lat = _.get(firstLocationResult, "lat", "");
        const lng = _.get(firstLocationResult, "lng", "");
        let addressObject = { lat: lat, lng: lng };
        if (
          eventType === MAP_EVENT_CLICK ||
          eventType === MAP_EVENT_MARKER_DRAG
        ) {
          addressObject.locAddress = _.get(results[0], "formatted_address", "");
        }

        try {
          if (firstAddressComponent && firstAddressComponent.length > 0) {
            const street_no = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "street_number";
            });

            if (street_no.length) {
              addressObject.street_no = _.get(street_no?.[0], "short_name", "");
            }

            const route = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "route";
            });

            if (route.length) {
              addressObject.route = _.get(route?.[0], "short_name", "");
              addressObject.street = _.get(route?.[0], "short_name", "");
            }
            const locality = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "locality";
            });
            if (locality.length) {
              addressObject.city = _.get(locality?.[0], "short_name", "");
            }
            const county = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "administrative_area_level_2";
            });
            if (county.length) {
              addressObject.county = _.get(county?.[0], "short_name", "");
            }
            const state = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "administrative_area_level_1";
            });
            if (state.length) {
              addressObject.state = _.get(state?.[0], "short_name", "");
            }
            const zip = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "postal_code";
            });
            if (zip.length) {
              addressObject.zip = _.get(zip?.[0], "short_name", "");
            }
          }
        } catch {}
        const street_no = _.get(addressObject, "street_no", null);
        const route = _.get(addressObject, "route", null);
        let streetAddress = null;
        if (street_no && route) {
          streetAddress = street_no + " " + route;
        } else if (street_no && !route) {
          streetAddress = street_no;
        } else if (!street_no && route) {
          streetAddress = route;
        }
        handleFormValues(addressObject);
        const reqPayLoad = {
          latitude: lat,
          longitude: lng,
          radius: INVENTORY_MAP_RADIUS,
          street: streetAddress,
        };
        if (eventType !== MAP_EVENT_LOCATION_CHANGE) {
          getPropertySearchDetails(reqPayLoad);
        }
      }
    });
  };

  return (
    <>
      {isAgentCreated == true && (
        <Form.Label className={locationError && "red-marked-formlabel"}>
          Address
        </Form.Label>
      )}
      <div className="position-relative">
        {addressList.length > 0 ? (
          <Button
            variant=""
            className="search-clear-btn"
            onClick={() => clearAddressList()}
          ></Button>
        ) : (
          ""
        )}
        <InputGroupControl
          inputGroupClass={
            locationError
              ? "wantad-location-search position-relative mb-0 red-marked-search"
              : "wantad-location-search position-relative mb-0"
          }
          ariaLabel="Example"
          ariaDescribedBy="basic-addon3"
          errorMessage={addressError}
          iconClass="search-icon"
          placeholder={
            isAgentCreated == true ? "Enter address" : "Street address"
          }
          isIconEnable={true}
          iconId="button-addon3"
          value={formatAddressForDisplay(locationAddress) || ""}
          onChange={handleLocationChange}
          inputRef={addressRef}
          disabled={disabled}
        />{" "}
        {addressList.length > 0 ? (
          <div className="location-result-wrap mw-100">
            <ul>
              {addressList?.map((address, i) => {
                return (
                  <li key={i}>
                    <a
                      role="button"
                      href={void 0}
                      onClick={(e) => {
                        handleItemSelection(address);
                      }}
                    >
                      {formatAddressForDisplay(
                        _.get(address, "formatted_address", ""),
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          ""
        )}
      </div>

      <div className="googlemap-responsive google-map-padding mb-16">
        <div className="map-outer mt-12">
          <MapBoxComponent
            center={[defaultProps.center.lng, defaultProps.center.lat]}
            handleItemSelection={handleItemSelection}
            isEditable
            getDetailedAddress
          />
        </div>
      </div>
    </>
  );
};

export default InventoryLocationMap;
